import { DBT, Phrase } from 'types/internal'
import { TwoFactorT } from 'types/twoFactor'
import orderBy from 'lodash/orderBy'
import forEach from 'lodash/forEach'
import first from 'lodash/first'
import last from 'lodash/last'
import get from 'lodash/get'
import map from 'lodash/map'
import compact from 'lodash/compact'

export const getDemo2Screenshot = (interaction: TwoFactorT.T) => {
  const screenshots = orderBy(interaction.screenshots, 'createdAt', 'desc')
  const knownUserScreenshot: DBT.ScreenshotT[] = []
  const faceScreenshot: DBT.ScreenshotT[] = []
  const noFaceScreenshot: DBT.ScreenshotT[] = []

  forEach(screenshots, (s) => {
    if (s.aiAnalysis) {
      if (s.userId) {
        knownUserScreenshot.push(s)
      } else if (s.faceId) {
        faceScreenshot.push(s)
      } else {
        noFaceScreenshot.push(s)
      }
    }
  })

  const availableScreenshotsList = [
    ...knownUserScreenshot,
    ...faceScreenshot,
    ...noFaceScreenshot,
    first(screenshots)
  ]

  const screenshot = first(compact(availableScreenshotsList))

  return screenshot
}

export const getSessionDuration = (session: TwoFactorT.T) => {
  let lastPhraseTime

  const st = last(session.steps)
  lastPhraseTime = get(st, 'createdAt')

  if (lastPhraseTime) {
    return lastPhraseTime - session.createdAt
  } else {
    return 0
  }
}

export const createSessionTranscript = (session: TwoFactorT.T): Phrase[] => {
  return compact(
    map(session.steps, (st) => {
      if (
        st.sType === TwoFactorT.StepTypeT.QUESTION ||
        st.sType === TwoFactorT.StepTypeT.REPLY
      ) {
        const p: Phrase = {
          id: st.id,
          createdAt: st.createdAt,
          text: st.text,
          isAvatar: st.sType === TwoFactorT.StepTypeT.QUESTION
        }
        return p
      } else {
        return null
      }
    })
  )
}

// Utility for fuzzy matching field names to handle variations and typos

// Calculate Levenshtein distance between two strings
function levenshteinDistance(str1: string, str2: string): number {
  const m = str1.length
  const n = str2.length
  const dp: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0))

  for (let i = 0; i <= m; i++) dp[i][0] = i
  for (let j = 0; j <= n; j++) dp[0][j] = j

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1]
      } else {
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1, // deletion
          dp[i][j - 1] + 1, // insertion
          dp[i - 1][j - 1] + 1 // substitution
        )
      }
    }
  }

  return dp[m][n]
}

// Normalize field name for comparison (lowercase, remove special chars)
function normalizeFieldName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[_-]/g, '') // Remove underscores and hyphens
    .replace(/\s+/g, '') // Remove spaces
}

// Check if a field name matches any whitelisted field with fuzzy matching
export function isFieldWhitelisted(
  fieldName: string,
  whitelist: string[]
): boolean {
  const normalizedField = normalizeFieldName(fieldName)

  for (const whitelistItem of whitelist) {
    const normalizedWhitelist = normalizeFieldName(whitelistItem)

    // Exact match after normalization
    if (normalizedField === normalizedWhitelist) {
      return true
    }

    // Calculate similarity based on Levenshtein distance
    const distance = levenshteinDistance(normalizedField, normalizedWhitelist)
    const maxLength = Math.max(
      normalizedField.length,
      normalizedWhitelist.length
    )
    const similarity = 1 - distance / maxLength

    // If similarity is > 80%, consider it a match (handles typos)
    if (similarity > 0.8) {
      return true
    }
  }

  return false
}

// Common field name variations mapping
const fieldVariations: Record<string, string[]> = {
  firstName: ['firstname', 'first_name', 'fname', 'givenname', 'given_name'],
  lastName: [
    'lastname',
    'last_name',
    'lname',
    'surname',
    'familyname',
    'family_name'
  ],
  email: ['e-mail', 'emailaddress', 'email_address', 'mail'],
  phone: ['telephone', 'phonenumber', 'phone_number', 'mobile', 'cell'],
  company: ['companyname', 'company_name', 'organization', 'org'],
  jobTitle: ['jobtitle', 'job_title', 'position', 'role', 'title'],
  linkedinUrl: [
    'linkedin',
    'linkedinurl',
    'linkedin_url',
    'personLinkedinUrl',
    'person_linkedin_url'
  ],
  companyLinkedinUrl: [
    'company_linkedin',
    'companylinkedin',
    'company_linkedin_url'
  ],
  website: ['url', 'web', 'homepage', 'site']
}

// Get the canonical field name for display
export function getCanonicalFieldName(
  fieldName: string,
  whitelist: string[]
): string {
  const normalizedField = normalizeFieldName(fieldName)

  // Check for exact matches first
  for (const whitelistItem of whitelist) {
    if (normalizeFieldName(whitelistItem) === normalizedField) {
      return whitelistItem
    }
  }

  // Check common variations
  for (const [canonical, variations] of Object.entries(fieldVariations)) {
    if (variations.some((v) => normalizeFieldName(v) === normalizedField)) {
      return canonical
    }
  }

  // Return original if no match found
  return fieldName
}
